import {
  ButtonComponent,
  ButtonType,
  TemplateType,
} from '@components/AdminHome/Channels/WhatsApp/WhatsAppTemplates/types';
import {
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  makeStyles,
  Typography,
  withStyles,
} from '@material-ui/core';
import { blueGrey } from '@material-ui/core/colors';
import Tooltip_ from '@material-ui/core/Tooltip';
import { ReplyRounded } from '@material-ui/icons';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionSharp';
import ImageOutlinedIcon from '@material-ui/icons/ImageSharp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PhoneIcon from '@material-ui/icons/Phone';
import RenderMarkdown from '@src/components/RTEditor/RenderMarkdown';
import { getFileName } from '@src/utils/fileUtils';
import { WhatsAppTemplate } from '@types';

export type BroadcastTemplatePreviewProps = {
  template: WhatsAppTemplate;
  summary?: boolean;
};

const Tooltip = withStyles(() => ({
  tooltip: {
    fontSize: '0.85rem',
  },
}))(Tooltip_);

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 255,
    background: 'transparent',
    '& p': {
      whiteSpace: 'pre-wrap',
      hyphens: 'auto',
      '-moz-hyphens': 'none',
      cursor: 'pointer',
    },
  },
  summary: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    flex: '1',
    flexWrap: 'wrap',
    paddingBottom: '1rem',
    '& .MuiChip-root': {
      marginRight: '4px',
      height: '20px',
    },
  },
  templateName: {
    marginRight: '4px',
    flex: '1',
    wordWrap: 'break-word',
  },
  headerMedia: {
    '& a': {
      display: 'flex',
      flexFlow: 'column nowrap',
      justifyContent: 'center',
      alignItems: 'center',
      flex: 1,
      height: 170,
      '& > button': {
        marginTop: '0.5rem',
      },
    },
    height: 170,
    backgroundColor:
      theme.palette.type === 'dark' ? theme.palette.grey[800] : blueGrey['50'],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '3rem',
  },
  footer: {
    marginTop: '1rem',
  },
  actions: {
    padding: 0,
    '& .MuiButtonGroup-root': {
      flex: '1',
      justifyContent: 'stretch',
      margin: '0',
    },
    '& .MuiButton-root': {
      flex: '1',
      background: theme.palette.background.paper,
      color: theme.palette.primary.main,
      border: '0',
      borderRadius: '0',
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
}));

const StartButtonIcon: React.FC<{ type: ButtonType }> = ({ type }) => {
  if (type === ButtonType.PHONE_NUMBER) return <PhoneIcon fontSize="small" />;
  if (type === ButtonType.URL) return <OpenInNewIcon fontSize="small" />;
  if (type === ButtonType.QUICK_REPLY) return <ReplyRounded fontSize="small" />;
  return null;
};

const BroadcastTemplatePreview: React.FC<BroadcastTemplatePreviewProps> = ({
  template,
  summary,
}) => {
  const data = template.components;
  const bodyText = template.messageWithPlaceHolders;

  const HEADER = data && data?.find(item => item.type === TemplateType.HEADER);
  const FOOTER = data && data?.find(item => item.type === TemplateType.FOOTER);
  const component =
    data && data?.find(item => item.type === TemplateType.BUTTONS);

  const withFlowButtons = component?.buttons?.[0]?.type === ButtonType.FLOW;

  const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
  const staticUrl = HEADER?.url || '';

  const classes = useStyles();

  return (
    <Card className={classes.root} variant={summary ? 'outlined' : 'elevation'}>
      {!summary && (
        <>
          {HEADER?.format === 'DOCUMENT' && (
            <Tooltip
              title={`Header Document ${staticUrl ? 'Static' : ''}`}
              placement="right"
              arrow
            >
              <CardMedia className={classes.headerMedia}>
                <a
                  download={`${template.name}-HEADER`}
                  href={staticUrl as string}
                  style={{ pointerEvents: staticUrl ? 'auto' : 'none' }}
                >
                  <DescriptionOutlinedIcon color="action" fontSize="inherit" />
                  <Button variant="outlined" size="small">
                    {staticUrl ? 'Static Document' : 'Dynamic Document'}
                  </Button>
                </a>
              </CardMedia>
            </Tooltip>
          )}
          {HEADER?.format === 'IMAGE' && (
            <Tooltip
              title={`Header Image ${staticUrl ? 'Static' : ''}`}
              placement="right"
              arrow
            >
              <CardMedia className={classes.headerMedia}>
                {allowedExtensions.test(staticUrl) ? (
                  <img
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%',
                    }}
                    alt={getFileName(staticUrl)}
                    src={staticUrl}
                  />
                ) : (
                  <ImageOutlinedIcon color="action" fontSize="inherit" />
                )}
              </CardMedia>
            </Tooltip>
          )}
        </>
      )}
      <CardContent>
        {summary && (
          <div className={classes.summary}>
            <Typography className={classes.templateName}>
              <b>{template.name}</b>
            </Typography>
            <div>
              {HEADER && (
                <Chip
                  size="small"
                  color="default"
                  label={`Header:${HEADER.format}`}
                />
              )}
              {FOOTER && <Chip size="small" label="Footer" />}
              {component?.buttons && !withFlowButtons && (
                <Chip size="small" label="Buttons" />
              )}
              {component?.buttons && withFlowButtons && (
                <Chip size="small" label="Flow" />
              )}
            </div>
          </div>
        )}
        {!summary && HEADER?.format === 'TEXT' && (
          <Tooltip title="Header Text" placement="right" arrow>
            <Typography variant="body2" gutterBottom>
              <b>{HEADER.text}</b>
            </Typography>
          </Tooltip>
        )}
        {bodyText && (
          <Tooltip
            title="Body"
            placement="right"
            arrow
            disableHoverListener={summary}
          >
            <Typography variant="body2" gutterBottom>
              <RenderMarkdown markdown={bodyText} />
            </Typography>
          </Tooltip>
        )}
        {!summary && FOOTER && (
          <Tooltip title="Footer" placement="right" arrow>
            <Typography
              className={classes.footer}
              variant="body2"
              gutterBottom
              color="textSecondary"
            >
              {FOOTER.text}
            </Typography>
          </Tooltip>
        )}
      </CardContent>
      {!summary && component?.buttons && (
        <>
          <Divider style={{ height: 1 }} />
          <CardActions disableSpacing className={classes.actions}>
            <ButtonGroup variant="text" orientation="vertical" color="primary">
              {component.buttons.map((item: ButtonComponent) => {
                const url =
                  item.type === ButtonType.QUICK_REPLY ? '' : item.url;
                return (
                  <Tooltip
                    key={item.id}
                    title={
                      <span>
                        Button: <u>{url}</u>
                      </span>
                    }
                    placement="right"
                    arrow
                  >
                    <Button
                      startIcon={
                        <StartButtonIcon type={item.type as ButtonType} />
                      }
                    >
                      <Typography variant="body2" title={url}>
                        {item.text}
                      </Typography>
                    </Button>
                  </Tooltip>
                );
              })}
            </ButtonGroup>
          </CardActions>
        </>
      )}
    </Card>
  );
};

export default BroadcastTemplatePreview;
